import { mediaMax, Icon } from "@customer-tools-web/component-ui";
import { Drawer as AntdDrawer } from "antd";
import { DrawerProps } from "antd/lib/drawer";
import * as React from "react";
import styled from "styled-components";

export type TypeOmit<T, K> = Pick<T, Exclude<keyof T, K>>;

export const useDrawer = () => {
  const [showDrawer, setShowDrawer] = React.useState(false);
  const closeDrawer = () => setShowDrawer(false);
  return {
    renderDrawer: (props: TypeOmit<Props, "visible" | "onClose">) => (
      <Drawer {...props} onClose={closeDrawer} visible={showDrawer} />
    ),
    showDrawer: () => setShowDrawer(true),
    closeDrawer,
    isDrawerOpen: showDrawer,
  };
};

interface Props extends DrawerProps {
  visible: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}
export const Drawer: React.FC<Props> = (props) => {
  return (
    <DrawerWrapper
      {...props}
      title={
        <>
          <MenuTitle>
            <CloseButton onClick={props.onClose}>
              <Icon type="close" />
            </CloseButton>
            {props.title}
          </MenuTitle>
        </>
      }
      closable={false}
      className="header-menu-drawer"
      width={320}
      placement={props.placement || "left"}
    >
      {props.children}
      {props.footer}
    </DrawerWrapper>
  );
};

const MenuTitle = styled.div`
  position: relative;
  min-height: 24px;
`;

const CloseButton = styled.div`
  position: absolute;
  right: -8px;
  cursor: pointer;
  ${Icon} {
    font-size: ${(props) => props?.theme?.fontSize?.title2};
    color: ${(props) => props.theme.textColor};
  }
`;

const DrawerWrapper = styled(AntdDrawer)`
  .ant-drawer-wrapper-body {
    display: flex;
    flex-direction: column;
    .ant-drawer-header {
      flex: none;
    }
    .ant-drawer-body {
      flex: 1;
      padding: 0;

      display: flex;
      flex-direction: column;

      .ant-menu {
        flex: 1 0 auto;
      }
    }
  }
  .ant-menu-root {
    &.ant-menu-inline {
      > li {
        margin: 0;
        height: auto;
        border-bottom: 1px solid ${(props) => props.theme.bgDeepGray};
      }
      .submenu-item-title-text,
      a {
        color: ${(props) => props.theme.textDeepGray};
        text-decoration: none;
      }

      li.ant-menu-item.ant-menu-item-selected,
      li.ant-menu-item.ant-menu-item-active,
      li.ant-menu-submenu-selected .ant-menu-submenu-title,
      li.ant-menu-submenu-active .ant-menu-submenu-title {
        background: ${(props) => props.theme.white};
        :after {
          display: none;
        }
        .submenu-item-title-text,
        a {
          color: ${(props) => props.theme.menuActiveColor};
        }
      }
      ul.ant-menu-sub li.ant-menu-item a {
        color: ${(props) => props.theme.textColor};
      }
      ul.ant-menu-sub li.ant-menu-item.ant-menu-item-selected,
      ul.ant-menu-sub li.ant-menu-item.ant-menu-item-active {
        background: ${(props) => props.theme.white};
        :after {
          display: none;
        }
        a {
          color: ${(props) => props.theme.menuActiveColor};
        }
      }
      > li.ant-menu-item {
        padding: 16px 0;
        padding-left: 16px !important;
        font-weight: 600;
        font-size: ${(props) => props?.theme?.fontSize?.body};
        line-height: 2.4rem;

        i {
          font-weight: 500;
        }
      }
      > li.ant-menu-submenu {
        padding-left: 16px !important;
        > div.ant-menu-submenu-title {
          margin: 0;
          padding: 16px 0;
          padding-left: 0 !important;
          font-weight: 600;
          font-size: ${(props) => props?.theme?.fontSize?.body};
          line-height: 2.4rem;
          height: auto;
        }
        > ul.ant-menu-sub {
          margin-top: -8px;
          > li.ant-menu-item {
            :first-child {
              display: none;
            }
            font-weight: 400;
            font-size: ${(props) => props?.theme?.fontSize?.body};
            line-height: 2rem;
            height: auto;
            padding: 8px;
            padding-left: 16px !important;
            margin: 0;
            ${mediaMax.xs`
              font-size: ${(props: any) => props?.theme?.fontSize?.caption};
            `};
          }
        }
        .ant-menu-submenu-arrow {
          right: 20px;
          ::after,
          ::before {
            width: 8px;
            background-image: ${({ theme }) =>
              `linear-gradient(to right, ${theme.textDeepGray}, ${theme.textDeepGray})`};
          }
          ::before {
            transform: rotate(-45deg) translateX(3px);
          }
          ::after {
            transform: rotate(45deg) translateX(-3px);
          }
        }
        &.ant-menu-submenu-open {
          padding-bottom: 8px;
          .ant-menu-submenu-arrow::before {
            transform: rotate(45deg) translateX(3px);
          }
          .ant-menu-submenu-arrow::after {
            transform: rotate(-45deg) translateX(-3px);
          }
        }
      }
    }
  }
`;
